import { and, ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { useJsonForms, withJsonFormsControlProps } from '@jsonforms/react'

import { styled, Checkbox } from 'opensolar-ui'
import { default as React } from 'react'

const Container = styled('div', { name: 'CategorizedListContainer' })(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
}))

const CategoryContainer = styled('div', { name: 'CategoryContainer' })(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 120,
  flex: '0 0 auto',
}))

const CategoryTitle = styled('h4', { name: 'CategoryTitle' })(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}))

const ItemsGrid = styled('div', { name: 'ItemsGrid' })(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  flexDirection: 'column',
  maxHeight: 170,
  alignContent: 'flex-start',
  '@media (min-width: 1200px)': {
    maxHeight: 120,
  },
  '@media (max-width: 500px)': {
    maxHeight: 'unset',
  },
}))

const CheckboxLabel = styled('label', { name: 'CheckboxLabel' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginRight: theme.spacing(1),
}))

const tester = rankWith(100, and(uiTypeIs('CategorizedList')))

const Renderer: React.FC<ControlProps> = (props) => {
  const { core } = useJsonForms()
  const { handleChange, path, uischema } = props
  const data = core?.data[path]

  const categories = uischema?.options?.categories
  const labels = uischema?.options?.labels || {}
  const getLabel = (value: string) => labels[value] || value

  const toggleValue = (value) => {
    const newValues = data?.includes(value) ? data.filter((v) => v !== value) : [...(data || []), value]
    handleChange(path, newValues)
  }

  const value_map = uischema?.options?.value_map
  const getAllowedItems = (): string[] => {
    if (!value_map || !value_map.scope || !Array.isArray(value_map.map)) {
      return []
    }

    const scopeField = value_map.scope.replace('#/properties/', '')
    const currentValue = core?.data?.[scopeField]

    // If no scope value is selected, allow all items by default
    if (!currentValue) {
      const all = ([] as string[]).concat(...Object.values(categories as Record<string, string[]>))
      return all
    }

    const mapping = value_map.map.find((item) => item.scope_value === currentValue)
    return mapping?.enabled || []
  }

  React.useEffect(() => {
    if (!value_map || !Array.isArray(data)) return

    const allowedItems = getAllowedItems()
    const cleaned = data.filter((item: string) => allowedItems.includes(item))

    if (cleaned.length !== data.length) {
      handleChange(path, cleaned)
    }
  }, [data, path, value_map])

  const isDisabled = (placement: string) => (value_map ? !getAllowedItems().includes(placement) : false)

  return (
    <Container>
      {categories &&
        Object.entries(categories).map(([category, items]) => (
          <CategoryContainer key={`placement-${category}`}>
            <CategoryTitle>{category}</CategoryTitle>
            <ItemsGrid>
              {(items as string[]).map((option) => (
                <CheckboxLabel key={option}>
                  <Checkbox
                    style={{ padding: 0 }}
                    checked={Array.isArray(data) ? data.includes(option) : false}
                    onChange={() => toggleValue(option)}
                    disabled={isDisabled(option)}
                  />
                  <span>{getLabel(option)}</span>
                </CheckboxLabel>
              ))}
            </ItemsGrid>
          </CategoryContainer>
        ))}
    </Container>
  )
}

const renderer = withJsonFormsControlProps(Renderer)

export default {
  tester,
  renderer,
}
